import React, { useState, useEffect, useMemo } from 'react';
import Swal from 'sweetalert2';
import {
  obtenerArmazones,
  editarArmazon,
  agregarArmazon,
  eliminarArmazon,
  subirImagenArmazon,
  obtenerCatalogosArmazones,
  crearCatalogoArmazonItem,
  obtenerConfiguracionTienda
} from '../api/backend_v2';
import TiendaDisenoModal from '../components/TiendaDisenoModal';
import { getStorefrontUrl } from '../utils/storefront';
import { obtenerProveedores } from '../utils/proveedores';
import { calcularPrecioEfectivoConDescuento, cargarDescuentoEfectivoSeguro, DESCUENTO_EFECTIVO_DEFAULT } from '../utils/precios';
import {
  MagnifyingGlassIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  PhotoIcon,
  ShoppingBagIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  CloudArrowUpIcon,
  SparklesIcon,
  FunnelIcon,
  EyeIcon,
  BanknotesIcon,
  TagIcon,
  GlobeAltIcon,
  LinkIcon,
  XMarkIcon,
  ArrowTopRightOnSquareIcon,
  SwatchIcon
} from '@heroicons/react/24/outline';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const resolveImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('/uploads/')) return `${API_URL}${url}`;
  if (url.includes('drive.google.com') || url.includes('googleusercontent.com')) {
    let fileId = null;
    const matchFile = url.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
    if (matchFile) fileId = matchFile[1];
    if (!fileId) {
      const matchId = url.match(/[?&]id=([a-zA-Z0-9_-]+)/);
      if (matchId) fileId = matchId[1];
    }
    if (!fileId) {
      const matchLh3 = url.match(/\/d\/([a-zA-Z0-9_-]+)/);
      if (matchLh3) fileId = matchLh3[1];
    }
    if (fileId) {
      return `https://lh3.googleusercontent.com/d/${fileId}`;
    }
  }
  return url;
};

const parseFotos = (fotosRaw) => {
  if (!fotosRaw) return [];
  if (Array.isArray(fotosRaw)) return fotosRaw.map(resolveImageUrl);
  if (typeof fotosRaw === 'string') {
    try {
      const parsed = JSON.parse(fotosRaw);
      if (Array.isArray(parsed)) return parsed.map(resolveImageUrl);
    } catch {
      if (fotosRaw.startsWith('http') || fotosRaw.startsWith('/')) {
        return [resolveImageUrl(fotosRaw)];
      }
    }
  }
  return [];
};

const PublicacionesEcommerce = () => {
  const [armazones, setArmazones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busqueda, setBusqueda] = useState('');
  const [filtroEcom, setFiltroEcom] = useState('todos'); // 'todos' | 'publicados' | 'no-publicados'
  const [filtroML, setFiltroML] = useState('todos'); // 'todos' | 'ml'
  const [filtroFotos, setFiltroFotos] = useState('todos'); // 'todos' | 'con-fotos' | 'sin-fotos'
  const [filtroGenero, setFiltroGenero] = useState('todos');
  const [filtroTipo, setFiltroTipo] = useState('todos');
  const [pagina, setPagina] = useState(1);
  const tamanoPagina = 12;

  // Catálogos
  const [marcas, setMarcas] = useState([]);
  const [materiales, setMateriales] = useState([]);
  const [colores, setColores] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [descuentoEfectivo, setDescuentoEfectivo] = useState(DESCUENTO_EFECTIVO_DEFAULT);

  // Modal Foto & Info Web
  const [modalFotoItem, setModalFotoItem] = useState(null);
  const [fotosLocales, setFotosLocales] = useState([]);
  const [urlManual, setUrlManual] = useState('');
  const [descripcionWeb, setDescripcionWeb] = useState('');
  const [generoWeb, setGeneroWeb] = useState('Unisex');
  const [tipoArmazonWeb, setTipoArmazonWeb] = useState('Receta');
  const [subiendoFoto, setSubiendoFoto] = useState(false);
  const [guardandoWebInfo, setGuardandoWebInfo] = useState(false);

  // Modal Alta Armazón
  const [modalNuevo, setModalNuevo] = useState(false);
  const [modalDiseno, setModalDiseno] = useState(false);
  const [slugTienda, setSlugTienda] = useState('');
  useEffect(() => {
    obtenerConfiguracionTienda()
      .then((data) => {
        if (data?.slug) setSlugTienda(data.slug);
      })
      .catch(() => {});
  }, []);
  const urlTiendaDinamica = getStorefrontUrl(slugTienda);

  const handleAbrirTiendaOnline = () => {
    window.open(urlTiendaDinamica, '_blank');
  };

  const [formNuevo, setFormNuevo] = useState({
    marca: '',
    modelo: '',
    color: '',
    material: '',
    cantidad: '1',
    medidas: '',
    tipo_bisagra: '',
    costo: '',
    precio_publico: '',
    precio_efectivo: '',
    id_proveedor: '',
    codigo_ean: '',
    sol: false,
    en_ml: false,
    en_ecom: true,
    genero: 'Unisex',
    tipo_armazon: 'Receta',
    descripcion: ''
  });
  const [precioEfectivoManual, setPrecioEfectivoManual] = useState(false);
  const [guardandoNuevo, setGuardandoNuevo] = useState(false);

  // Cargar datos al montar
  const cargarDatos = async () => {
    setLoading(true);
    try {
      const [resArmazones, resCat, resProv, descEff] = await Promise.all([
        obtenerArmazones(),
        obtenerCatalogosArmazones(true).catch(() => ({ marcas: [], materiales: [], colores: [] })),
        obtenerProveedores().catch(() => []),
        cargarDescuentoEfectivoSeguro()
      ]);

      const listaArmazones = resArmazones?.armazones || (Array.isArray(resArmazones) ? resArmazones : []);
      setArmazones(listaArmazones);
      setMarcas(resCat.marcas || []);
      setMateriales(resCat.materiales || []);
      setColores(resCat.colores || []);
      setProveedores(resProv || []);
      setDescuentoEfectivo(descEff || DESCUENTO_EFECTIVO_DEFAULT);
    } catch (err) {
      console.error('Error cargando datos de e-commerce:', err);
      Swal.fire('Error', 'No se pudieron cargar los productos para el e-commerce', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarDatos();
  }, []);

  // Métricas rápidas
  const metricas = useMemo(() => {
    const total = armazones.length;
    const publicadosEcom = armazones.filter(a => a.en_ecom).length;
    const publicadosML = armazones.filter(a => a.en_ml).length;
    const conFotos = armazones.filter(a => parseFotos(a.fotos).length > 0).length;
    const sinFotos = total - conFotos;
    const stockTotal = armazones.reduce((acc, a) => acc + (parseInt(a.cantidad) || 0), 0);
    return { total, publicadosEcom, publicadosML, conFotos, sinFotos, stockTotal };
  }, [armazones]);

  // Filtrado de armazones
  const armazonesFiltrados = useMemo(() => {
    return armazones.filter(item => {
      const q = busqueda.toLowerCase().trim();
      const matchBusqueda = !q || (
        (item.marca || '').toLowerCase().includes(q) ||
        (item.modelo || '').toLowerCase().includes(q) ||
        (item.color || '').toLowerCase().includes(q) ||
        (item.material || '').toLowerCase().includes(q)
      );

      const matchEcom =
        filtroEcom === 'todos' ? true :
        filtroEcom === 'publicados' ? Boolean(item.en_ecom) : !item.en_ecom;

      const matchML =
        filtroML === 'todos' ? true : Boolean(item.en_ml);

      const fotosList = parseFotos(item.fotos);
      const matchFotos =
        filtroFotos === 'todos' ? true :
        filtroFotos === 'con-fotos' ? fotosList.length > 0 : fotosList.length === 0;

      const matchGenero = filtroGenero === 'todos' ? true : (item.genero || 'Unisex') === filtroGenero;
      const matchTipo = filtroTipo === 'todos' ? true : (item.tipo_armazon || 'Receta') === filtroTipo;

      return matchBusqueda && matchEcom && matchML && matchFotos && matchGenero && matchTipo;
    });
  }, [armazones, busqueda, filtroEcom, filtroML, filtroFotos, filtroGenero, filtroTipo]);

  // Paginación
  const armazonesPaginados = useMemo(() => {
    const inicio = (pagina - 1) * tamanoPagina;
    return armazonesFiltrados.slice(inicio, inicio + tamanoPagina);
  }, [armazonesFiltrados, pagina]);

  const totalPaginas = Math.ceil(armazonesFiltrados.length / tamanoPagina) || 1;

  // Toggle rápido de E-Commerce (en_ecom)
  const handleToggleEcom = async (item) => {
    const nuevoEstado = !item.en_ecom;

    // Advertencia de regla de negocio si activa sin fotos
    const fotos = parseFotos(item.fotos);
    if (nuevoEstado && fotos.length === 0) {
      const result = await Swal.fire({
        title: '⚠️ Armazón sin fotos',
        text: 'La tienda pública solo mostrará armazones que tengan al menos una foto cargada. ¿Deseas activar la publicación de todos modos?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sí, activar',
        cancelButtonText: 'Cancelar'
      });
      if (!result.isConfirmed) return;
    }

    // Actualización optimista
    setArmazones(prev => prev.map(a => a.id === item.id ? { ...a, en_ecom: nuevoEstado } : a));

    try {
      await editarArmazon(item.id, { en_ecom: nuevoEstado });
      const toast = Swal.mixin({
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 2000,
        timerProgressBar: true
      });
      toast.fire({
        icon: 'success',
        title: nuevoEstado ? 'Publicado en E-Commerce' : 'Pausado de E-Commerce'
      });
    } catch (err) {
      console.error('Error al cambiar estado de e-commerce:', err);
      // Revertir optimismo
      setArmazones(prev => prev.map(a => a.id === item.id ? { ...a, en_ecom: !nuevoEstado } : a));
      Swal.fire('Error', 'No se pudo actualizar el estado en el servidor', 'error');
    }
  };

  // Abrir modal de Fotos e Info Web
  const handleAbrirModalWeb = (item) => {
    setModalFotoItem(item);
    setFotosLocales(parseFotos(item.fotos));
    setDescripcionWeb(item.descripcion || '');
    setGeneroWeb(item.genero || 'Unisex');
    setTipoArmazonWeb(item.tipo_armazon || (item.sol ? 'Sol' : 'Receta'));
    setUrlManual('');
  };

  // Agregar foto desde URL directa o Drive
  const handleAgregarUrlManual = () => {
    if (!urlManual.trim()) return;
    const resolved = resolveImageUrl(urlManual.trim());
    if (fotosLocales.includes(resolved)) {
      Swal.fire('Atención', 'Esa foto ya está agregada', 'info');
      return;
    }
    setFotosLocales(prev => [...prev, resolved]);
    setUrlManual('');
  };

  // Subir archivo de imagen al backend (Drive o local)
  const handleSubirArchivo = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setSubiendoFoto(true);
    try {
      const subidas = [];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const res = await subirImagenArmazon(file);
        if (res && res.url) {
          subidas.push(resolveImageUrl(res.url));
        }
      }
      setFotosLocales(prev => [...prev, ...subidas]);
      Swal.fire({
        icon: 'success',
        title: 'Imágenes subidas',
        text: `Se agregaron ${subidas.length} imagen(es) correctamente.`,
        timer: 1500,
        showConfirmButton: false
      });
    } catch (err) {
      console.error('Error al subir imagen:', err);
      Swal.fire('Error', err.message || 'No se pudo subir la imagen', 'error');
    } finally {
      setSubiendoFoto(false);
      e.target.value = '';
    }
  };

  // Eliminar foto de la lista modal
  const handleEliminarFotoLocal = (index) => {
    setFotosLocales(prev => prev.filter((_, i) => i !== index));
  };

  // Guardar Cambios Web del Modal
  const handleGuardarWebInfo = async () => {
    if (!modalFotoItem) return;

    setGuardandoWebInfo(true);
    try {
      const payload = {
        fotos: JSON.stringify(fotosLocales),
        descripcion: descripcionWeb,
        genero: generoWeb,
        tipo_armazon: tipoArmazonWeb
      };

      await editarArmazon(modalFotoItem.id, payload);

      setArmazones(prev => prev.map(a => a.id === modalFotoItem.id ? {
        ...a,
        fotos: payload.fotos,
        descripcion: payload.descripcion,
        genero: payload.genero,
        tipo_armazon: payload.tipo_armazon
      } : a));

      setModalFotoItem(null);
      Swal.fire('Guardado', 'Información web e imágenes actualizadas con éxito', 'success');
    } catch (err) {
      console.error('Error al guardar datos web:', err);
      Swal.fire('Error', 'No se pudieron guardar las modificaciones', 'error');
    } finally {
      setGuardandoWebInfo(false);
    }
  };

  // Autocalcular precio efectivo en Modal Nuevo Armazón
  const handlePrecioPublicoChangeNuevo = (val) => {
    setFormNuevo(prev => {
      const updated = { ...prev, precio_publico: val };
      if (!precioEfectivoManual && val) {
        const pub = parseFloat(val) || 0;
        updated.precio_efectivo = String(calcularPrecioEfectivoConDescuento(pub, descuentoEfectivo));
      }
      return updated;
    });
  };

  // Guardar Nuevo Armazón E-Commerce
  const handleGuardarNuevoArmazon = async (e) => {
    e.preventDefault();
    if (!formNuevo.marca || !formNuevo.modelo || !formNuevo.color || !formNuevo.material) {
      Swal.fire('Atención', 'Por favor completa Marca, Modelo, Color y Material', 'warning');
      return;
    }

    setGuardandoNuevo(true);
    try {
      const payload = {
        marca: formNuevo.marca.trim(),
        modelo: formNuevo.modelo.trim(),
        color: formNuevo.color.trim(),
        material: formNuevo.material.trim(),
        cantidad: parseInt(formNuevo.cantidad) || 0,
        medidas: formNuevo.medidas || '',
        tipo_bisagra: formNuevo.tipo_bisagra || '',
        costo: formNuevo.costo ? parseFloat(formNuevo.costo) : null,
        precio_publico: formNuevo.precio_publico ? parseFloat(formNuevo.precio_publico) : null,
        precio_efectivo: formNuevo.precio_efectivo ? parseFloat(formNuevo.precio_efectivo) : null,
        id_proveedor: formNuevo.id_proveedor ? parseInt(formNuevo.id_proveedor) : null,
        codigo_ean: formNuevo.codigo_ean || null,
        sol: Boolean(formNuevo.sol),
        en_ml: Boolean(formNuevo.en_ml),
        en_ecom: Boolean(formNuevo.en_ecom),
        genero: formNuevo.genero || 'Unisex',
        tipo_armazon: formNuevo.tipo_armazon || 'Receta',
        descripcion: formNuevo.descripcion || '',
        fotos: '[]'
      };

      const result = await agregarArmazon(payload);
      Swal.fire('¡Éxito!', 'Armazón creado correctamente para el E-Commerce', 'success');
      setModalNuevo(false);
      setFormNuevo({
        marca: '',
        modelo: '',
        color: '',
        material: '',
        cantidad: '1',
        medidas: '',
        tipo_bisagra: '',
        costo: '',
        precio_publico: '',
        precio_efectivo: '',
        id_proveedor: '',
        codigo_ean: '',
        sol: false,
        en_ml: false,
        en_ecom: true,
        genero: 'Unisex',
        tipo_armazon: 'Receta',
        descripcion: ''
      });
      cargarDatos();
    } catch (err) {
      console.error('Error al crear armazón:', err);
      Swal.fire('Error', err.message || 'No se pudo crear el armazón', 'error');
    } finally {
      setGuardandoNuevo(false);
    }
  };

  // Eliminar armazón
  const handleEliminar = async (item) => {
    const res = await Swal.fire({
      title: '¿Eliminar armazón?',
      text: `¿Estás seguro de eliminar ${item.marca} ${item.modelo}? Esta acción no se puede deshacer.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    });

    if (res.isConfirmed) {
      try {
        await eliminarArmazon(item.id);
        setArmazones(prev => prev.filter(a => a.id !== item.id));
        Swal.fire('Eliminado', 'El armazón ha sido eliminado', 'success');
      } catch (err) {
        Swal.fire('Error', 'No se pudo eliminar el armazón', 'error');
      }
    }
  };

  return (
    <div className="space-y-6 pb-12">
      {/* Header con título y botón de acción */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-gradient-to-r from-blue-900 via-indigo-900 to-slate-900 p-6 rounded-2xl text-white shadow-xl">
        <div>
          <div className="flex items-center gap-3">
            <ShoppingBagIcon className="w-8 h-8 text-blue-400" />
            <h1 className="text-2xl font-bold tracking-tight">Vendedor E-Commerce</h1>
            <span className="bg-blue-500/20 text-blue-300 text-xs font-semibold px-2.5 py-1 rounded-full border border-blue-400/30">
              Gestión de Tienda
            </span>
          </div>
          <p className="text-blue-200/80 text-sm mt-1">
            Administra la visibilidad pública de tus armazones (<code className="text-amber-300">en_ecom</code>), sube imágenes directas o a tu Google Drive y configura descripciones.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={cargarDatos}
            disabled={loading}
            className="p-2.5 rounded-xl bg-white/10 hover:bg-white/20 text-white transition-all duration-200 border border-white/10"
            title="Recargar catálogo"
          >
            <ArrowPathIcon className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>

          <button
            onClick={() => setModalNuevo(true)}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white font-semibold px-4 py-2.5 rounded-xl shadow-lg shadow-blue-600/30 hover:shadow-blue-600/50 transition-all duration-200"
          >
            <PlusIcon className="w-5 h-5" />
            <span>Nuevo Armazón Web</span>
          </button>

          <button
            onClick={() => setModalDiseno(true)}
            className="flex items-center gap-2 bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-500 hover:to-indigo-600 text-white font-semibold px-4 py-2.5 rounded-xl shadow-lg shadow-indigo-600/30 hover:shadow-indigo-600/50 transition-all duration-200"
            title="Personalizar diseño y colores de tu tienda online"
          >
            <SwatchIcon className="w-5 h-5 text-cyan-300" />
            <span>Diseño de Tienda</span>
          </button>

          <button
            onClick={handleAbrirTiendaOnline}
            className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold px-4 py-2.5 rounded-xl shadow-lg shadow-emerald-600/30 hover:shadow-emerald-600/50 transition-all duration-200"
            title="Abrir la vidriera web pública de tu óptica"
          >
            <GlobeAltIcon className="w-5 h-5 text-emerald-200" />
            <span>Ver Tienda Online</span>
            <ArrowTopRightOnSquareIcon className="w-4 h-4 text-emerald-200" />
          </button>
        </div>
      </div>

      {/* Tarjetas de Métricas Resumen */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        <div className="bg-white dark:bg-slate-800 p-4 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700/60">
          <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Total en Inventario</p>
          <p className="text-2xl font-bold text-slate-800 dark:text-white mt-1">{metricas.total}</p>
          <div className="text-[11px] text-slate-400 mt-0.5">{metricas.stockTotal} un. en stock</div>
        </div>

        <div className="bg-emerald-50 dark:bg-emerald-950/30 p-4 rounded-2xl border border-emerald-200 dark:border-emerald-800/40">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold text-emerald-700 dark:text-emerald-300">Visibles E-Com</p>
            <GlobeAltIcon className="w-4 h-4 text-emerald-600" />
          </div>
          <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-300 mt-1">{metricas.publicadosEcom}</p>
          <div className="text-[11px] text-emerald-600/80 dark:text-emerald-400 mt-0.5">Activos (en_ecom)</div>
        </div>

        <div className="bg-amber-50 dark:bg-amber-950/30 p-4 rounded-2xl border border-amber-200 dark:border-amber-800/40">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold text-amber-700 dark:text-amber-300">Mercado Libre</p>
            <TagIcon className="w-4 h-4 text-amber-600" />
          </div>
          <p className="text-2xl font-bold text-amber-700 dark:text-amber-300 mt-1">{metricas.publicadosML}</p>
          <div className="text-[11px] text-amber-600/80 dark:text-amber-400 mt-0.5">Publicados (en_ml)</div>
        </div>

        <div className="bg-indigo-50 dark:bg-indigo-950/30 p-4 rounded-2xl border border-indigo-200 dark:border-indigo-800/40">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold text-indigo-700 dark:text-indigo-300">Con Imágenes</p>
            <PhotoIcon className="w-4 h-4 text-indigo-600" />
          </div>
          <p className="text-2xl font-bold text-indigo-700 dark:text-indigo-300 mt-1">{metricas.conFotos}</p>
          <div className="text-[11px] text-indigo-600/80 dark:text-indigo-400 mt-0.5">Listos para tienda</div>
        </div>

        <div className="bg-rose-50 dark:bg-rose-950/30 p-4 rounded-2xl border border-rose-200 dark:border-rose-800/40">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold text-rose-700 dark:text-rose-300">Sin Imágenes</p>
            <XCircleIcon className="w-4 h-4 text-rose-600" />
          </div>
          <p className="text-2xl font-bold text-rose-700 dark:text-rose-300 mt-1">{metricas.sinFotos}</p>
          <div className="text-[11px] text-rose-600/80 dark:text-rose-400 mt-0.5">Requieren foto</div>
        </div>

        <div className="bg-blue-50 dark:bg-blue-950/30 p-4 rounded-2xl border border-blue-200 dark:border-blue-800/40">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold text-blue-700 dark:text-blue-300">Desc. Efectivo</p>
            <BanknotesIcon className="w-4 h-4 text-blue-600" />
          </div>
          <p className="text-2xl font-bold text-blue-700 dark:text-blue-300 mt-1">{descuentoEfectivo}%</p>
          <div className="text-[11px] text-blue-600/80 dark:text-blue-400 mt-0.5">Configurado en Óptica</div>
        </div>
      </div>

      {/* Barra de Filtros y Búsqueda */}
      <div className="bg-white dark:bg-slate-800 p-4 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700/60 space-y-3">
        <div className="flex flex-col lg:flex-row gap-3 items-stretch lg:items-center justify-between">
          {/* Buscador */}
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="w-5 h-5 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Buscar por marca, modelo, color o material..."
              value={busqueda}
              onChange={(e) => { setBusqueda(e.target.value); setPagina(1); }}
              className="w-full pl-10 pr-4 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-800 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            />
          </div>

          {/* Filtros Dropdowns */}
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={filtroEcom}
              onChange={(e) => { setFiltroEcom(e.target.value); setPagina(1); }}
              className="px-3 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-medium text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="todos">Tienda E-Com: Todos</option>
              <option value="publicados">🟢 Visibles en E-Com (en_ecom=True)</option>
              <option value="no-publicados">⚪ Ocultos en E-Com (en_ecom=False)</option>
            </select>

            <select
              value={filtroFotos}
              onChange={(e) => { setFiltroFotos(e.target.value); setPagina(1); }}
              className="px-3 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-medium text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="todos">Fotos: Todas</option>
              <option value="con-fotos">🖼️ Con Fotos</option>
              <option value="sin-fotos">⚠️ Sin Fotos</option>
            </select>

            <select
              value={filtroGenero}
              onChange={(e) => { setFiltroGenero(e.target.value); setPagina(1); }}
              className="px-3 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-medium text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="todos">Género: Todos</option>
              <option value="Unisex">Unisex</option>
              <option value="Hombre">Hombre</option>
              <option value="Mujer">Mujer</option>
              <option value="Niño">Niño</option>
            </select>

            <select
              value={filtroTipo}
              onChange={(e) => { setFiltroTipo(e.target.value); setPagina(1); }}
              className="px-3 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-medium text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="todos">Tipo: Todos</option>
              <option value="Receta">Receta</option>
              <option value="Sol">Sol</option>
            </select>
          </div>
        </div>
      </div>

      {/* Grilla de Armazones */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700/60">
          <ArrowPathIcon className="w-10 h-10 text-blue-600 animate-spin mb-3" />
          <p className="text-slate-500 dark:text-slate-400 font-medium">Cargando catálogo para E-Commerce...</p>
        </div>
      ) : armazonesPaginados.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700/60 text-center px-4">
          <ShoppingBagIcon className="w-16 h-16 text-slate-300 dark:text-slate-600 mb-3" />
          <h3 className="text-lg font-semibold text-slate-800 dark:text-white">No se encontraron armazones</h3>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 max-w-md">
            Intenta cambiar los filtros de búsqueda o crea un nuevo armazón habilitado para e-commerce.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
          {armazonesPaginados.map(item => {
            const fotos = parseFotos(item.fotos);
            const tieneFotos = fotos.length > 0;
            const imagenPrincipal = tieneFotos ? fotos[0] : null;

            return (
              <div
                key={item.id}
                className={`group bg-white dark:bg-slate-800 rounded-2xl border transition-all duration-200 shadow-sm hover:shadow-md flex flex-col justify-between overflow-hidden ${
                  item.en_ecom
                    ? 'border-emerald-300 dark:border-emerald-600/50 ring-1 ring-emerald-500/10'
                    : 'border-slate-200 dark:border-slate-700/60'
                }`}
              >
                <div>
                  {/* Encabezado e Imagen Principal */}
                  <div className="relative bg-slate-100 dark:bg-slate-900/60 aspect-video flex items-center justify-center overflow-hidden">
                    {imagenPrincipal ? (
                      <img
                        src={imagenPrincipal}
                        alt={`${item.marca} ${item.modelo}`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="flex flex-col items-center justify-center text-slate-400 dark:text-slate-500 p-4">
                        <PhotoIcon className="w-10 h-10 mb-1" />
                        <span className="text-xs font-medium">Sin foto cargada</span>
                      </div>
                    )}

                    {/* Badges Flotantes */}
                    <div className="absolute top-2 left-2 flex flex-col gap-1.5">
                      {item.sol ? (
                        <span className="bg-amber-500/90 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-sm">
                          SOL ☀️
                        </span>
                      ) : (
                        <span className="bg-blue-600/90 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-sm">
                          RECETA 👓
                        </span>
                      )}
                    </div>

                    <div className="absolute top-2 right-2 flex items-center gap-1">
                      {item.en_ml && (
                        <span className="bg-amber-400 text-slate-900 text-[10px] font-bold px-2 py-0.5 rounded-full shadow-sm" title="Publicado en Mercado Libre">
                          ML 🛒
                        </span>
                      )}
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full shadow-sm ${
                        tieneFotos ? 'bg-indigo-600 text-white' : 'bg-rose-500 text-white'
                      }`}>
                        {fotos.length} 📷
                      </span>
                    </div>
                  </div>

                  {/* Detalles del Producto */}
                  <div className="p-4 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <span className="text-[11px] font-bold tracking-wider text-blue-600 dark:text-blue-400 uppercase">
                          {item.marca}
                        </span>
                        <h4 className="text-base font-bold text-slate-900 dark:text-white line-clamp-1">
                          {item.modelo}
                        </h4>
                      </div>
                      <div className="text-right">
                        <span className="text-xs text-slate-400 block">Stock</span>
                        <span className={`text-sm font-bold ${item.cantidad > 0 ? 'text-slate-800 dark:text-slate-200' : 'text-rose-500'}`}>
                          {item.cantidad} un.
                        </span>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2 text-xs text-slate-500 dark:text-slate-400 pt-1 border-t border-slate-100 dark:border-slate-700/50">
                      <div>
                        <span className="block text-[10px] text-slate-400">Color / Material</span>
                        <span className="font-medium text-slate-700 dark:text-slate-300 truncate block">
                          {item.color} - {item.material}
                        </span>
                      </div>
                      <div>
                        <span className="block text-[10px] text-slate-400">Género</span>
                        <span className="font-medium text-slate-700 dark:text-slate-300">
                          {item.genero || 'Unisex'}
                        </span>
                      </div>
                    </div>

                    {/* Precios */}
                    <div className="bg-slate-50 dark:bg-slate-900/40 p-2.5 rounded-xl flex items-center justify-between mt-2">
                      <div>
                        <span className="text-[10px] text-slate-400 block">Precio Público</span>
                        <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                          ${item.precio_publico ? item.precio_publico.toLocaleString('es-AR') : '0'}
                        </span>
                      </div>
                      <div className="text-right">
                        <span className="text-[10px] text-emerald-600 font-medium block">Efectivo ({descuentoEfectivo}%)</span>
                        <span className="text-sm font-bold text-emerald-600 dark:text-emerald-400">
                          ${item.precio_efectivo ? item.precio_efectivo.toLocaleString('es-AR') : '0'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer de Acciones y Switch de Publicación en E-Com */}
                <div className="p-3 bg-slate-50 dark:bg-slate-800/80 border-t border-slate-100 dark:border-slate-700/60 flex items-center justify-between gap-2">
                  {/* Switch interactivo en_ecom */}
                  <label className="relative inline-flex items-center cursor-pointer select-none" title="Activar/Desactivar visibilidad en la tienda web">
                    <input
                      type="checkbox"
                      checked={Boolean(item.en_ecom)}
                      onChange={() => handleToggleEcom(item)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-slate-300 peer-focus:outline-none rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500"></div>
                    <span className="ml-2 text-xs font-bold text-slate-700 dark:text-slate-200">
                      {item.en_ecom ? 'Publicado' : 'Pausado'}
                    </span>
                  </label>

                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleAbrirModalWeb(item)}
                      className="p-2 rounded-xl bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 hover:bg-blue-200 transition-colors"
                      title="Gestionar Fotos e Info Web"
                    >
                      <PhotoIcon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleEliminar(item)}
                      className="p-2 rounded-xl bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300 hover:bg-rose-200 transition-colors"
                      title="Eliminar armazón"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Controles de Paginación */}
      {totalPaginas > 1 && (
        <div className="flex items-center justify-between bg-white dark:bg-slate-800 p-4 rounded-2xl border border-slate-200 dark:border-slate-700/60">
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Mostrando página <span className="font-semibold text-slate-700 dark:text-slate-200">{pagina}</span> de <span className="font-semibold text-slate-700 dark:text-slate-200">{totalPaginas}</span>
          </p>
          <div className="flex items-center gap-2">
            <button
              disabled={pagina === 1}
              onClick={() => setPagina(p => Math.max(1, p - 1))}
              className="px-3 py-1.5 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 rounded-lg text-xs font-semibold text-slate-700 dark:text-slate-200 disabled:opacity-40"
            >
              Anterior
            </button>
            <button
              disabled={pagina === totalPaginas}
              onClick={() => setPagina(p => Math.min(totalPaginas, p + 1))}
              className="px-3 py-1.5 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 rounded-lg text-xs font-semibold text-slate-700 dark:text-slate-200 disabled:opacity-40"
            >
              Siguiente
            </button>
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* MODAL GESTIÓN DE FOTOS E INFORMACIÓN WEB */}
      {/* ========================================================= */}
      {modalFotoItem && (
        <div
          className="fixed inset-0 z-[1050] flex items-center justify-center p-3 sm:p-4 bg-slate-950/80 backdrop-blur-sm overflow-y-auto overscroll-contain animate-fadeIn"
          onClick={(e) => {
            if (e.target === e.currentTarget && !guardandoFotos) {
              setModalFotoItem(null);
            }
          }}
        >
          <div
            className="bg-white dark:bg-slate-800 rounded-3xl shadow-2xl max-w-3xl w-full p-6 space-y-6 border border-slate-200 dark:border-slate-700/80 my-auto max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-700 pb-4">
              <div>
                <div className="flex items-center gap-2">
                  <PhotoIcon className="w-6 h-6 text-blue-600" />
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white">
                    Fotos e Información Web: {modalFotoItem.marca} {modalFotoItem.modelo}
                  </h3>
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  Sube fotos a tu Drive/servidor o ingresa enlaces para la tienda online.
                </p>
              </div>
              <button
                onClick={() => setModalFotoItem(null)}
                className="p-2 rounded-xl text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            {/* Galería de Fotos Existentes */}
            <div className="space-y-3">
              <label className="text-sm font-bold text-slate-800 dark:text-slate-200 block">
                Galería de Imágenes del Producto ({fotosLocales.length})
              </label>

              {fotosLocales.length === 0 ? (
                <div className="p-8 border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-2xl text-center">
                  <PhotoIcon className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-2" />
                  <p className="text-sm font-medium text-slate-600 dark:text-slate-400">Sin fotos cargadas aún</p>
                  <p className="text-xs text-slate-400 mt-1">Sube una imagen desde tu dispositivo o ingresa la URL de Google Drive abajo.</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {fotosLocales.map((url, idx) => (
                    <div key={idx} className="relative group rounded-xl overflow-hidden aspect-video border border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-900">
                      <img src={url} alt={`Foto ${idx}`} className="w-full h-full object-cover" />
                      {idx === 0 && (
                        <span className="absolute top-1 left-1 bg-emerald-600 text-white text-[9px] font-bold px-1.5 py-0.5 rounded shadow">
                          PRINCIPAL
                        </span>
                      )}
                      <button
                        onClick={() => handleEliminarFotoLocal(idx)}
                        className="absolute top-1 right-1 bg-rose-600 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow"
                        title="Eliminar foto"
                      >
                        <XMarkIcon className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Opciones de Carga de Foto */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
              {/* Opción 1: Subir Archivo Directo (Google Drive o Servidor) */}
              <div className="bg-slate-50 dark:bg-slate-900/50 p-4 rounded-2xl border border-slate-200 dark:border-slate-700 space-y-2">
                <label className="text-xs font-bold text-slate-800 dark:text-slate-200 flex items-center gap-1.5">
                  <CloudArrowUpIcon className="w-4 h-4 text-blue-600" />
                  1. Subir Foto desde Archivo (Auto Drive/Servidor)
                </label>
                <p className="text-[11px] text-slate-500">Selecciona o arrastra una foto (.jpg, .png) desde tu dispositivo.</p>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleSubirArchivo}
                  disabled={subiendoFoto}
                  className="block w-full text-xs text-slate-500 file:mr-3 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-semibold file:bg-blue-600 file:text-white hover:file:bg-blue-500 cursor-pointer"
                />
                {subiendoFoto && <p className="text-xs text-blue-600 animate-pulse font-medium">Subiendo foto a Google Drive / servidor...</p>}
              </div>

              {/* Opción 2: Pegar URL Directa o Google Drive */}
              <div className="bg-slate-50 dark:bg-slate-900/50 p-4 rounded-2xl border border-slate-200 dark:border-slate-700 space-y-2">
                <label className="text-xs font-bold text-slate-800 dark:text-slate-200 flex items-center gap-1.5">
                  <LinkIcon className="w-4 h-4 text-indigo-600" />
                  2. Pegar Enlace de Google Drive / URL Web
                </label>
                <p className="text-[11px] text-slate-500">Pega un enlace compartido de Drive o URL directa de la imagen.</p>
                <div className="flex gap-2">
                  <input
                    type="url"
                    placeholder="https://drive.google.com/file/d/..."
                    value={urlManual}
                    onChange={(e) => setUrlManual(e.target.value)}
                    className="flex-1 px-3 py-1.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs"
                  />
                  <button
                    type="button"
                    onClick={handleAgregarUrlManual}
                    className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-semibold rounded-xl"
                  >
                    Agregar
                  </button>
                </div>
              </div>
            </div>

            {/* Atributos Adicionales de Tienda Web */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-slate-100 dark:border-slate-700">
              <div>
                <label className="text-xs font-bold text-slate-700 dark:text-slate-300 block mb-1">
                  Género del Armazón
                </label>
                <select
                  value={generoWeb}
                  onChange={(e) => setGeneroWeb(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs"
                >
                  <option value="Unisex">Unisex</option>
                  <option value="Hombre">Hombre</option>
                  <option value="Mujer">Mujer</option>
                  <option value="Niño">Niño</option>
                </select>
              </div>

              <div>
                <label className="text-xs font-bold text-slate-700 dark:text-slate-300 block mb-1">
                  Tipo de Armazón
                </label>
                <select
                  value={tipoArmazonWeb}
                  onChange={(e) => setTipoArmazonWeb(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs"
                >
                  <option value="Receta">Receta</option>
                  <option value="Sol">Sol</option>
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="text-xs font-bold text-slate-700 dark:text-slate-300 block mb-1">
                  Descripción Pública para la Tienda Web
                </label>
                <textarea
                  rows={3}
                  value={descripcionWeb}
                  onChange={(e) => setDescripcionWeb(e.target.value)}
                  placeholder="Escribe detalles atractivos sobre el diseño, estilo, materiales o patillas..."
                  className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs text-slate-800 dark:text-slate-100"
                />
              </div>
            </div>

            {/* Footer Modal */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100 dark:border-slate-700">
              <button
                type="button"
                onClick={() => setModalFotoItem(null)}
                className="px-4 py-2 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 text-slate-700 dark:text-slate-200 text-xs font-semibold rounded-xl"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleGuardarWebInfo}
                disabled={guardandoWebInfo}
                className="px-5 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold rounded-xl shadow-md disabled:opacity-50"
              >
                {guardandoWebInfo ? 'Guardando...' : 'Guardar Información Web'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* MODAL PERSONALIZACIÓN VISUAL Y COLORES DE TIENDA */}
      <TiendaDisenoModal
        isOpen={modalDiseno}
        onClose={() => setModalDiseno(false)}
        storefrontUrl={urlTiendaDinamica}
      />

      {/* MODAL CREACIÓN NUEVO ARMAZÓN E-COMMERCE */}
      {/* ========================================================= */}
      {modalNuevo && (
        <div
          className="fixed inset-0 z-[1050] flex items-center justify-center p-3 sm:p-4 bg-slate-950/80 backdrop-blur-sm overflow-y-auto overscroll-contain animate-fadeIn"
          onClick={(e) => {
            if (e.target === e.currentTarget && !guardandoNuevo) {
              setModalNuevo(false);
            }
          }}
        >
          <div
            className="bg-white dark:bg-slate-800 rounded-3xl shadow-2xl max-w-2xl w-full p-6 space-y-6 border border-slate-200 dark:border-slate-700/80 my-auto max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-700 pb-4">
              <div className="flex items-center gap-2">
                <PlusIcon className="w-6 h-6 text-blue-600" />
                <h3 className="text-xl font-bold text-slate-900 dark:text-white">Nuevo Armazón E-Commerce</h3>
              </div>
              <button onClick={() => setModalNuevo(false)} className="p-2 rounded-xl text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700">
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleGuardarNuevoArmazon} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold text-slate-700 dark:text-slate-300 block mb-1">Marca *</label>
                  <input
                    type="text"
                    required
                    list="marcas-list"
                    value={formNuevo.marca}
                    onChange={(e) => setFormNuevo(prev => ({ ...prev, marca: e.target.value }))}
                    placeholder="Ej. Ray-Ban"
                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs"
                  />
                  <datalist id="marcas-list">
                    {marcas.map(m => <option key={m.id} value={m.nombre} />)}
                  </datalist>
                </div>

                <div>
                  <label className="text-xs font-bold text-slate-700 dark:text-slate-300 block mb-1">Modelo *</label>
                  <input
                    type="text"
                    required
                    value={formNuevo.modelo}
                    onChange={(e) => setFormNuevo(prev => ({ ...prev, modelo: e.target.value }))}
                    placeholder="Ej. RB5154"
                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs"
                  />
                </div>

                <div>
                  <label className="text-xs font-bold text-slate-700 dark:text-slate-300 block mb-1">Color *</label>
                  <input
                    type="text"
                    required
                    list="colores-list"
                    value={formNuevo.color}
                    onChange={(e) => setFormNuevo(prev => ({ ...prev, color: e.target.value }))}
                    placeholder="Ej. Negro Mate"
                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs"
                  />
                  <datalist id="colores-list">
                    {colores.map(c => <option key={c.id} value={c.nombre} />)}
                  </datalist>
                </div>

                <div>
                  <label className="text-xs font-bold text-slate-700 dark:text-slate-300 block mb-1">Material *</label>
                  <input
                    type="text"
                    required
                    list="materiales-list"
                    value={formNuevo.material}
                    onChange={(e) => setFormNuevo(prev => ({ ...prev, material: e.target.value }))}
                    placeholder="Ej. Acetato"
                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs"
                  />
                  <datalist id="materiales-list">
                    {materiales.map(m => <option key={m.id} value={m.nombre} />)}
                  </datalist>
                </div>

                <div>
                  <label className="text-xs font-bold text-slate-700 dark:text-slate-300 block mb-1">Cantidad Stock *</label>
                  <input
                    type="number"
                    min="0"
                    required
                    value={formNuevo.cantidad}
                    onChange={(e) => setFormNuevo(prev => ({ ...prev, cantidad: e.target.value }))}
                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs"
                  />
                </div>

                <div>
                  <label className="text-xs font-bold text-slate-700 dark:text-slate-300 block mb-1">Precio Público ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formNuevo.precio_publico}
                    onChange={(e) => handlePrecioPublicoChangeNuevo(e.target.value)}
                    placeholder="0.00"
                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs"
                  />
                </div>

                <div>
                  <label className="text-xs font-bold text-slate-700 dark:text-slate-300 block mb-1">
                    Precio Efectivo (${descuentoEfectivo}% desc.)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formNuevo.precio_efectivo}
                    onChange={(e) => {
                      setPrecioEfectivoManual(true);
                      setFormNuevo(prev => ({ ...prev, precio_efectivo: e.target.value }));
                    }}
                    placeholder="Autocalculado"
                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs"
                  />
                </div>

                <div>
                  <label className="text-xs font-bold text-slate-700 dark:text-slate-300 block mb-1">Proveedor</label>
                  <select
                    value={formNuevo.id_proveedor}
                    onChange={(e) => setFormNuevo(prev => ({ ...prev, id_proveedor: e.target.value }))}
                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs"
                  >
                    <option value="">Seleccionar Proveedor...</option>
                    {proveedores.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                  </select>
                </div>
              </div>

              {/* Toggles E-Commerce & ML */}
              <div className="flex flex-wrap items-center gap-6 p-3 bg-slate-50 dark:bg-slate-900/50 rounded-2xl border border-slate-200 dark:border-slate-700">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formNuevo.en_ecom}
                    onChange={(e) => setFormNuevo(prev => ({ ...prev, en_ecom: e.target.checked }))}
                    className="w-4 h-4 text-emerald-600 rounded"
                  />
                  <span className="text-xs font-bold text-slate-800 dark:text-slate-200">🟢 Publicar en E-Commerce (en_ecom)</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formNuevo.en_ml}
                    onChange={(e) => setFormNuevo(prev => ({ ...prev, en_ml: e.target.checked }))}
                    className="w-4 h-4 text-amber-600 rounded"
                  />
                  <span className="text-xs font-bold text-slate-800 dark:text-slate-200">🛒 Publicado en Mercado Libre (en_ml)</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formNuevo.sol}
                    onChange={(e) => setFormNuevo(prev => ({ ...prev, sol: e.target.checked }))}
                    className="w-4 h-4 text-blue-600 rounded"
                  />
                  <span className="text-xs font-bold text-slate-800 dark:text-slate-200">☀️ Armazón de Sol</span>
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100 dark:border-slate-700">
                <button
                  type="button"
                  onClick={() => setModalNuevo(false)}
                  className="px-4 py-2 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 text-xs font-semibold rounded-xl"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={guardandoNuevo}
                  className="px-5 py-2 bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold rounded-xl shadow-md disabled:opacity-50"
                >
                  {guardandoNuevo ? 'Creando...' : 'Crear y Continuar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicacionesEcommerce;
